import { useState, useEffect } from "react";
import { Switch } from '@headlessui/react'
import Navbar from "../../../constants/Navbar";
import { useNavigate } from "react-router";
import Select from "../../common/Select";
import LocationPicker from "../../common/LocationPicker";
import CheckboxGroup from "./CheckboxGroup";
import { useLocation } from "react-router";
import { X, ArrowLeft } from "lucide-react";
import axios from "axios";
import Cookies from "js-cookie";
import { ApiService } from "../../../api";
import { useAlert } from "../../../constants/AlertProvider";
import CircularProgress from '@mui/material/CircularProgress';
import HomeNavbar from "../HomeNavbar";
import JobRoles from "../../../constants/JobRoles.json";

const JOB_TYPES = ['Full Time', 'Contract', 'Internship', 'Co-Founder'];
const COMPANY_SIZES = [
    '1-10 employees', '11-50 employees', '51-200 employees', '201-500 employees',
    '501-1000 employees', '1001-5000 employees', '5000+ employees',
];
const INVESTMENT_STAGES = ['NA', 'Seed', 'Series A', 'Series B', 'Growth', 'IPO', 'Acquired'];

const opportunityTypeOptions = [
    { value: "All", label: "All" },
    { value: "Full Time", label: "Full Time" },
    { value: "Part Time", label: "Part Time" },
    { value: "Volunteer", label: "Volunteer" },
    { value: "AI gig", label: "AI gig" },
];

const salaryOptions = [
    { value: "10K - 100K", label: "10K - 100K" },
    { value: "100K - 200K", label: "100K - 200K" },
    { value: "200K - 300K", label: "200K - 300K" },
    { value: "More than 300K", label: "More than 300K" },
];

export default function OnboardingPreferences() {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(true);
    const [roleInput, setRoleInput] = useState("");
    const [roles, setRoles] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const isEditMode = location.state?.from === "editpreferences";
    const showAlert = useAlert();

    const [preferences, setPreferences] = useState({
        locations: [],
        opportunity_type: "All",
        preferred_salary: "10K - 100K",
        job_types: [],
        company_sizes: [],
        investment_stages: [],
        open_to_remote: false,
        requires_visa_sponsorship: false,
    });

    useEffect(() => {
        const fetchPreferences = async () => {
            setIsLoading(true);
            try {
                const res = await ApiService.apiCandidatePreferencesRetrieve();

                const prefs = res.preferences || {};
                const choices = res.choices || {};

                // Sanitize and set preferences
                setPreferences({
                    locations: prefs.locations || [],
                    opportunity_type: prefs.opportunity_type || "All",
                    preferred_salary: prefs.preferred_salary || "10K - 100K",
                    job_types: prefs.job_types || [],
                    company_sizes: prefs.company_sizes || [],
                    investment_stages: prefs.investment_stages || [],
                    open_to_remote: prefs.open_to_remote ?? false,
                    requires_visa_sponsorship: prefs.requires_visa_sponsorship ?? false,
                });

                // Handle roles
                if (prefs.role) {
                    setRoles(
                        prefs.role
                            .split(",")
                            .map((r) => r.trim())
                            .filter(Boolean)
                    );
                }

            } catch (error) {
                console.error("Failed to fetch preferences:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPreferences();
    }, []);

    const handleRoleInputChange = (e) => {
        const value = e.target.value;
        setRoleInput(value);
        if (value.trim().length > 0) {
            const filteredRoles = JobRoles.filter((role) =>
                role.title.toLowerCase().includes(value.toLowerCase()) && !roles.includes(role.title)
            ).map((role) => role.title);
            setSuggestions(filteredRoles.slice(0, 10));
        } else {
            setSuggestions([]);
        }
    };

    const handleSelectRole = (role) => {
        if (!roles.includes(role)) {
            setRoles([...roles, role]);
        }
        setRoleInput("");
        setSuggestions([]);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && roleInput.trim() !== "") {
            e.preventDefault();
            if (!roles.includes(roleInput.trim())) {
                setRoles([...roles, roleInput.trim()]);
            }
            setRoleInput("");
            setSuggestions([]);
        }
    };

    const removeRole = (role) => {
        setRoles(roles.filter((r) => r !== role));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPreferences(prev => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = (e) => {
        const { name, value, checked } = e.target;
        setPreferences(prev => {
            const currentValues = prev[name] || [];
            if (checked) {
                return { ...prev, [name]: [...currentValues, value] };
            }
            return { ...prev, [name]: currentValues.filter(item => item !== value) };
        });
    };

    const handleSwitchChange = (field, value) => {
        setPreferences(prev => ({ ...prev, [field]: value }));
    };

    const handleClearCheckboxes = (fieldName) => {
        setPreferences(prev => ({ ...prev, [fieldName]: [] }));
    };

    const handleSelectAllCheckboxes = (fieldName, options) => {
        setPreferences(prev => ({ ...prev, [fieldName]: [...options] }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!roles || roles.length === 0) {
            showAlert("Please enter at least one role.", "error");
            return;
        }

        if (!preferences.locations || preferences.locations.length === 0) {
            showAlert("Please select at least one location.", "error");
            return;
        }
        if (!preferences.opportunity_type) {
            showAlert("Please select an opportunity type.", "error");
            return;
        }
        if (!preferences.preferred_salary) {
            showAlert("Please select a preferred salary.", "error");
            return;
        }
        const payload = {
            ...preferences,
            role: roles.join(', '),
        };
        try {
            await ApiService.apiCandidatePreferencesUpdate({ requestBody: payload });
            // Actually kick off a matching run so the just-saved preferences take
            // effect (#297). Fire-and-forget: matching is async, so we don't block
            // navigation on it; the backend derives the filters from preferences.
            axios
                .post(
                    "/api/matching/match/",
                    { match_type: "job" },
                    { headers: { "X-CSRFToken": Cookies.get("csrftoken") }, withCredentials: true },
                )
                .catch((err) => console.error("Failed to trigger matching:", err));
            navigate("/home");
        } catch (error) {
            console.error("Failed to save preferences:", error);
            // Surface it -- previously the save failed silently (e.g. a 400), so the
            // button looked like it did nothing.
            showAlert(
                "Couldn't update your preferences. Please review your selections and try again.",
                "error",
            );
        }
    };

    if (isLoading) {
        return (
            <>
                {isEditMode ? <HomeNavbar /> : <Navbar />}
                <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center ${isEditMode && "mt-14"}`}>
                    <h1 className="text-2xl font-bold">Loading Your Preferences...</h1>
                </div>
            </>
        );
    }

    return (
        <>
            {isEditMode ? <HomeNavbar /> : <Navbar />}
            <form onSubmit={handleSave}>
                <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 ${isEditMode && "mt-14"}`}>
                    <div className="mb-10">
                        {isEditMode && (
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="flex items-center gap-2 mb-4 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-full transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                                Go back
                            </button>
                        )}
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">{isEditMode ? "Edit Preferences" : "Help us find your match!"}</h1>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-10">
                        <div className="lg:col-span-1">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Preferences</h3>
                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">This will be used to match you with the right opportunities.</p>
                        </div>

                        <div className="lg:col-span-2 space-y-8">
                            {/* Basic Info */}
                            <div className="space-y-6">
                                <div className="flex flex-col gap-y-2">
                                    <label htmlFor="role" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        Role*
                                    </label>

                                    <div className="flex flex-wrap items-center gap-2 border border-gray-300 dark:border-gray-600 p-2 rounded-lg">
                                        {roles.map((role, index) => (
                                            <span
                                                key={index}
                                                className="flex items-center gap-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full text-sm"
                                            >
                                                {role}
                                                <button
                                                    type="button"
                                                    onClick={() => removeRole(role)}
                                                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </span>
                                        ))}

                                        <div className="relative flex-1 min-w-[100px]">
                                            <input
                                                id="role"
                                                value={roleInput}
                                                onChange={handleRoleInputChange}
                                                onKeyDown={handleKeyDown}
                                                className="w-full p-1 text-sm bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none"
                                                placeholder="Software Engineer..."
                                                autoComplete="off"
                                            />
                                            {suggestions.length > 0 && (
                                                <ul className="absolute top-full left-0 mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 shadow-lg max-h-48 overflow-y-auto z-10">
                                                    {suggestions.map((role, index) => (
                                                        <li
                                                            key={index}
                                                            onClick={() => handleSelectRole(role)}
                                                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-900 dark:text-gray-100"
                                                        >
                                                            {role}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-y-2">
                                    <label
                                        htmlFor="locations"
                                        className="text-sm font-medium text-gray-900 dark:text-gray-100"
                                    >
                                        What locations do you want to work in?*
                                    </label>
                                    <LocationPicker
                                        value={preferences.locations}
                                        onChange={(locations) =>
                                            setPreferences((prev) => ({ ...prev, locations }))
                                        }
                                    />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <Select
                                        label="Opportunity Type*"
                                        name="opportunity_type"
                                        value={preferences.opportunity_type}
                                        onChange={handleChange}
                                        options={opportunityTypeOptions}
                                    />
                                    <Select
                                        label="Preferred Salary"
                                        name="preferred_salary"
                                        value={preferences.preferred_salary}
                                        onChange={handleChange}
                                        options={salaryOptions}
                                    />
                                </div>
                            </div>

                            <hr className="border-gray-200 dark:border-gray-700" />

                            {/* Job & Company Details */}
                            <div className="space-y-8">
                                <CheckboxGroup title="Job Types" name="job_types" options={JOB_TYPES} checkedValues={preferences.job_types} onChange={handleCheckboxChange} onClear={handleClearCheckboxes} onSelectAll={handleSelectAllCheckboxes} />
                                <CheckboxGroup title="Company Size" name="company_sizes" options={COMPANY_SIZES} checkedValues={preferences.company_sizes} onChange={handleCheckboxChange} onClear={handleClearCheckboxes} onSelectAll={handleSelectAllCheckboxes} />
                                <CheckboxGroup title="Investment Stage" name="investment_stages" options={INVESTMENT_STAGES} checkedValues={preferences.investment_stages} onChange={handleCheckboxChange} onClear={handleClearCheckboxes} onSelectAll={handleSelectAllCheckboxes} />
                            </div>

                            <hr className="border-gray-200 dark:border-gray-700" />

                            {/* Toggles */}
                            <div className="space-y-6">
                                <SwitchControl label="Remote culture" description="Only show jobs at companies that are mostly or fully remote" checked={preferences.open_to_remote} onChange={(val) => handleSwitchChange('open_to_remote', val)} />
                                <SwitchControl label="Immigration" description="Only show companies that can sponsor a visa" checked={preferences.requires_visa_sponsorship} onChange={(val) => handleSwitchChange('requires_visa_sponsorship', val)} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700">
                    <div className="max-w-7xl mx-auto flex justify-end p-6">
                        <button type="submit" className="text-white h-10 text-center font-medium text-sm bg-blue-700 px-6 rounded-lg cursor-pointer hover:bg-blue-600 disabled:bg-blue-400 disabled:cursor-not-allowed">
                            {isLoading ? (
                                <>
                                    <CircularProgress size={20} color="inherit" />
                                    Saving...
                                </>
                            ) : (
                                <>{isEditMode ? "Update Preferences" : "Start Matching!"}</>
                            )}
                        </button>
                    </div>
                </div>
            </form>
        </>
    );
}

const SwitchControl = ({ label, description, checked, onChange }) => (
    <div className="flex items-center justify-between">
        <div className="flex-grow pr-4">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
        </div>
        <Switch
            checked={checked}
            onChange={onChange}
            className={`${checked ? "bg-blue-600" : "bg-gray-200 dark:bg-gray-600"}
        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
        >
            <span
                aria-hidden="true"
                className={`${checked ? "translate-x-5" : "translate-x-0"}
          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
            />
        </Switch>
    </div>
);
